import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, UserCheck, CreditCard, KeyRound } from "lucide-react";
import SEO from "../../components/SEO";
import PageHeader from "../../components/PageHeader";
import { Reveal } from "../../components/Reveal";
import api, { formatApiError } from "../../lib/api";

const inputCls = "w-full border border-navy/20 bg-white px-4 py-3.5 text-sm text-navy focus:outline-none focus:border-rouge transition-colors duration-300";
const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy/70 mb-1.5";

function EmailMode() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/premiere-connexion", { email });
      setDone(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done?.found) {
    return (
      <div data-testid="creer-acces-success">
        <div className="w-14 h-14 bg-green-100 text-green-700 flex items-center justify-center mb-6">
          <Mail size={24} />
        </div>
        <p className="text-navy leading-relaxed">
          Nous avons trouvé votre inscription au Club Cycliste Le Boulou. Vous pouvez maintenant créer votre accès personnel.
        </p>
        <p className="mt-3 text-sm text-steel">Un email vient de vous être envoyé avec un lien sécurisé pour définir votre mot de passe. Pensez à vérifier vos courriers indésirables.</p>
      </div>
    );
  }
  if (done && !done.found) {
    return (
      <div data-testid="creer-acces-not-found">
        <div className="w-14 h-14 bg-amber-100 text-amber-700 flex items-center justify-center mb-6">
          <UserCheck size={24} />
        </div>
        <p className="text-navy leading-relaxed">
          Cette adresse email ne correspond pas encore à un adhérent enregistré dans notre base.
        </p>
        <p className="mt-3 text-sm text-steel">Vous souhaitez rejoindre le Club Cycliste Le Boulou ? Inscrivez-vous directement en ligne.</p>
        <Link
          to="/rejoindre"
          data-testid="creer-acces-join"
          className="mt-6 inline-flex items-center justify-center gap-2.5 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-navy transition-colors duration-300"
        >
          S'inscrire au club
        </Link>
      </div>
    );
  }
  return (
    <form onSubmit={submit} data-testid="creer-acces-form">
      <p className="text-steel text-sm leading-relaxed mb-6">
        Utilisez l'adresse email communiquée au club pour créer votre accès personnel.
      </p>
      <div className="mb-6">
        <label className={labelCls} htmlFor="creer-email">Email</label>
        <input id="creer-email" data-testid="creer-acces-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} autoComplete="email" />
      </div>
      {error && <p data-testid="creer-acces-error" className="mb-6 bg-rouge/15 border border-rouge/40 text-rouge text-sm px-4 py-3">{error}</p>}
      <button
        type="submit"
        disabled={busy}
        data-testid="creer-acces-submit"
        className="w-full inline-flex items-center justify-center gap-2.5 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-navy disabled:opacity-60 transition-colors duration-300"
      >
        {busy && <Loader2 size={16} className="animate-spin" />}
        Créer mon accès
      </button>
    </form>
  );
}

function LicenceMode() {
  const [licence, setLicence] = useState("");
  const [step, setStep] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [email2, setEmail2] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [created, setCreated] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/premiere-connexion-licence", { numero_licence: licence });
      setStep(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setError("");
    if (email.trim().toLowerCase() !== email2.trim().toLowerCase()) {
      setError("Les deux adresses email ne correspondent pas.");
      return;
    }
    if (password !== password2) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/creer-acces-licence", { numero_licence: licence, email: email.trim(), password });
      setCreated(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div data-testid="creer-acces-licence-success">
        <div className="w-14 h-14 bg-green-100 text-green-700 flex items-center justify-center mb-6">
          <KeyRound size={24} />
        </div>
        <p className="text-navy leading-relaxed font-semibold">Votre accès est créé !</p>
        <p className="mt-3 text-sm text-steel">
          Connectez-vous avec votre adresse email et votre mot de passe. Vous pourrez ensuite vous réinscrire
          pour la saison 2027 depuis votre tableau de bord.
        </p>
        <Link
          to="/espace/connexion"
          data-testid="creer-acces-licence-login"
          className="mt-6 inline-flex items-center justify-center gap-2.5 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-navy transition-colors duration-300"
        >
          Se connecter
        </Link>
      </div>
    );
  }

  if (step?.found && step.need_email) {
    return (
      <form onSubmit={create} data-testid="creer-acces-licence-form">
        <div className="w-14 h-14 bg-green-100 text-green-700 flex items-center justify-center mb-6">
          <UserCheck size={24} />
        </div>
        <p className="text-navy leading-relaxed mb-1 font-semibold" data-testid="licence-recognised">Licence reconnue — bienvenue {step.prenom} !</p>
        <p className="text-sm text-steel mb-6">Choisissez l'adresse email qui servira d'identifiant de connexion, puis votre mot de passe.</p>
        <div className="space-y-5 mb-6">
          <div>
            <label className={labelCls} htmlFor="lic-email">Email *</label>
            <input id="lic-email" data-testid="licence-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} autoComplete="email" />
          </div>
          <div>
            <label className={labelCls} htmlFor="lic-email2">Confirmation de l'email *</label>
            <input id="lic-email2" data-testid="licence-email-confirm" type="email" required value={email2} onChange={(e) => setEmail2(e.target.value)} className={inputCls} autoComplete="email" />
          </div>
          <div>
            <label className={labelCls} htmlFor="lic-pass">Mot de passe *</label>
            <input id="lic-pass" data-testid="licence-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} autoComplete="new-password" />
            <p className="mt-1.5 text-xs text-steel">8 caractères minimum, avec au moins une lettre et un chiffre.</p>
          </div>
          <div>
            <label className={labelCls} htmlFor="lic-pass2">Confirmation du mot de passe *</label>
            <input id="lic-pass2" data-testid="licence-password-confirm" type="password" required value={password2} onChange={(e) => setPassword2(e.target.value)} className={inputCls} autoComplete="new-password" />
          </div>
        </div>
        {error && <p data-testid="licence-create-error" className="mb-6 bg-rouge/15 border border-rouge/40 text-rouge text-sm px-4 py-3">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="licence-create-submit"
          className="w-full inline-flex items-center justify-center gap-2.5 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-navy disabled:opacity-60 transition-colors duration-300"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          Créer mon accès
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={verify} data-testid="licence-verify-form">
      <p className="text-steel text-sm leading-relaxed mb-6">
        Le club n'a pas votre adresse email ? Identifiez-vous avec votre <strong>numéro de licence FFC</strong>
        (il figure sur votre licence, format à 11 chiffres), puis choisissez votre email et votre mot de passe.
      </p>
      <div className="mb-6">
        <label className={labelCls} htmlFor="lic-num">Numéro de licence</label>
        <input id="lic-num" data-testid="licence-input" required value={licence} onChange={(e) => setLicence(e.target.value)} className={inputCls} placeholder="Ex. 51660430357" inputMode="numeric" />
      </div>
      {step && !step.found && (
        <p data-testid="licence-not-found" className="mb-6 bg-amber-50 border border-amber-300 text-amber-800 text-sm px-4 py-3">{step.message}</p>
      )}
      {step?.found && !step.need_email && (
        <div data-testid="licence-has-email" className="mb-6 bg-green-50 border border-green-300 text-green-800 text-sm px-4 py-3">
          <p>{step.message}</p>
          {step.has_account && (
            <Link to="/espace/connexion" data-testid="licence-goto-login" className="mt-2 inline-block font-semibold underline">Aller à la connexion</Link>
          )}
        </div>
      )}
      {error && <p data-testid="licence-verify-error" className="mb-6 bg-rouge/15 border border-rouge/40 text-rouge text-sm px-4 py-3">{error}</p>}
      <button
        type="submit"
        disabled={busy}
        data-testid="licence-verify-btn"
        className="w-full inline-flex items-center justify-center gap-2.5 bg-navy text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-rouge disabled:opacity-60 transition-colors duration-300"
      >
        {busy && <Loader2 size={16} className="animate-spin" />}
        Vérifier ma licence
      </button>
    </form>
  );
}

export default function EspaceCreerAcces() {
  const [mode, setMode] = useState("licence");

  return (
    <>
      <SEO title="Première connexion — Espace Licencié" />
      <PageHeader kicker="Espace Licencié" title="Première connexion ?" />

      <section className="py-16 sm:py-24 bg-mist">
        <div className="max-w-md mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="bg-white border border-navy/10 p-8 sm:p-10">
              <div className="w-14 h-14 bg-navy text-white flex items-center justify-center mb-6">
                {mode === "licence" ? <CreditCard size={24} /> : <Mail size={24} />}
              </div>
              <div className="grid grid-cols-2 gap-2 mb-8" data-testid="creer-acces-modes">
                <button
                  type="button"
                  onClick={() => setMode("licence")}
                  data-testid="mode-licence"
                  className={`font-heading uppercase tracking-wider text-xs px-4 py-3 border transition-[background-color,color,border-color] duration-300 ${mode === "licence" ? "bg-navy border-navy text-white" : "border-navy/20 text-navy/70 hover:border-rouge hover:text-rouge"}`}
                >
                  Avec mon n° de licence
                </button>
                <button
                  type="button"
                  onClick={() => setMode("email")}
                  data-testid="mode-email"
                  className={`font-heading uppercase tracking-wider text-xs px-4 py-3 border transition-[background-color,color,border-color] duration-300 ${mode === "email" ? "bg-navy border-navy text-white" : "border-navy/20 text-navy/70 hover:border-rouge hover:text-rouge"}`}
                >
                  Avec mon email
                </button>
              </div>
              {mode === "licence" ? <LicenceMode /> : <EmailMode />}
              <p className="mt-6 pt-6 border-t border-navy/10 text-sm">
                <Link to="/espace/connexion" className="text-navy/70 hover:text-rouge transition-colors" data-testid="creer-acces-back-login">
                  ← Retour à la connexion
                </Link>
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
